import React, { useEffect, useState, useCallback } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
    createPaymentReceiptByMesaIdV2,
    createPaymentReceiptByPedidoIdV2,
    createPaymentReceiptByPedidoGroupIdV2,
} from '../../../../../../../crud/payments_api/payment_receipts';
import { toast } from 'react-toastify';
import { FaExclamationTriangle, FaTags, FaArrowLeft } from 'react-icons/fa';
import { FaUsers, FaReceipt, FaHandHoldingUsd, FaFileInvoiceDollar } from 'react-icons/fa';
import './SplitMethod.scss';

interface SplitMethodProps {
    mesa_id?: number;
    pedido_group_id?: number;
    client_id?: number;
    client_name?: string;
    pedido?: any;
    mesa?: any;
    pedido_type?: string;
    externalSetPedidos?: (updateFunction: (prevPedidos: any) => any) => void;
    pedidos?: any; // Optional pedidos prop if not using context
    setPedidos: (pedidos: any) => void;
    setReceipts: (receipts: any[]) => void;
    setGoToPayment: (go: boolean) => void; // New prop to control payment view
    targetSelections?: string[]; // NEW: Array of selection_ids to be paid
    onHide?: () => void; // NEW: Callback to close the modal/go back
    hideUnicaOption?: boolean; // NEW: if true, hide "Cuenta Única" option (used in split button dropdown mode)
    preselectedMethod?: 'equal' | 'custom'; // NEW: pre-select a specific method
}


const SplitMethod: React.FC<SplitMethodProps> = ({
    mesa_id,
    pedido_group_id,
    client_id,
    client_name,
    pedido,
    mesa,
    pedido_type,
    externalSetPedidos,
    pedidos,
    setPedidos,
    setReceipts,
    setGoToPayment,
    targetSelections,
    onHide,
    hideUnicaOption = false,
    preselectedMethod

}) => {
    const [selectedSplitMethod, setSelectedSplitMethod] = useState(preselectedMethod || (hideUnicaOption ? 'equal' : 'single'));
    const [total_persons, setTotalPersons] = useState(1);
    const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false); // Loading state for button


    // Validation helper functions
    const validatePartesIguales = () => {
        return selectedSplitMethod === 'equal' && total_persons > 1;
    };

    const validatePersonalizado = () => {
        if (selectedSplitMethod !== 'custom') return true;
        return true

        /** We'll get back to this implememtation when have more time
        const allSelections: any[] = [];
        if (pedidos && Array.isArray(pedidos)) {
          pedidos.forEach((pedido: any) => {
            if (pedido.selections && Array.isArray(pedido.selections)) {
              allSelections.push(...pedido.selections);
            }
          });
        }
        
        // Check for unique client indices (excluding -1 which is "Grupo")
        const uniqueClientIndices = new Set(
          allSelections
            .map(sel => sel.clientIndex)
            .filter(index => index !== -1 && index !== undefined && index !== null)
        );
    
        
        
        return uniqueClientIndices.size > 1;
         */
    };

    const shouldShowPersonalizado = useCallback(() => {
        // Get all selections from all pedidos for this mesa
        const allSelections: any[] = [];
        if (pedidos && Array.isArray(pedidos)) {
            pedidos.forEach((pedido: any) => {
                if (pedido.selections && Array.isArray(pedido.selections)) {
                    allSelections.push(...pedido.selections);
                }
            });
        }

        // Only show Personalizado if there's more than one selection
        return allSelections.length > 1;
    }, [pedidos]);

    const isGenerarCuentaEnabled = () => {
        if (selectedSplitMethod === 'single') return true;
        if (selectedSplitMethod === 'equal') return validatePartesIguales();
        if (selectedSplitMethod === 'custom') return validatePersonalizado();
        return false;
    };

    const shouldShowWarning = () => {
        return selectedSplitMethod === 'custom' && !validatePersonalizado();
    };

    // Reset selected split method if custom is selected but no longer available
    useEffect(() => {
        if (selectedSplitMethod === 'custom' && !shouldShowPersonalizado()) {
            setSelectedSplitMethod('single');
        }
    }, [selectedSplitMethod, shouldShowPersonalizado]);

    // NEW: Auto-generate receipt when targetSelections is provided (from selection mode)
    useEffect(() => {
        if (targetSelections && targetSelections.length > 0 && mesa_id !== undefined) {
            // Automatically generate a 'single' receipt for the selected items
            const user = JSON.parse(localStorage.getItem('user') ?? '{}');
            const user_id = user.user_id ? Number(user.user_id) : 0;
            
            setIsGeneratingReceipt(true);
            
            createPaymentReceiptByMesaIdV2(mesa_id, 'single', user_id, 1, targetSelections)
                .then((response) => {
                    const mesasPaymentCache = JSON.parse(localStorage.getItem('mesasPaymentCache') || '{}');
                    if (mesa) mesa.charged = true;
                    setReceipts(response.data);
                    mesasPaymentCache[mesa_id] = {
                        splitMethod: 'single',
                        totalPersons: 1,
                        paidPersons: 0
                    };
                    localStorage.setItem('mesasPaymentCache', JSON.stringify(mesasPaymentCache));
                    // IMPORTANT: Set goToPayment BEFORE dispatching event to prevent race condition
                    setGoToPayment(true);
                    // Dispatch event to update UI in real-time (after goToPayment is set)
                    window.dispatchEvent(new CustomEvent('mesa-receipt-generated', { detail: { mesa_id } }));
                })
                .catch((error) => {
                    console.error('Error creating payment receipt for selected items: ', error);
                    setGoToPayment(false);
                    toast.error('Error creando recibo para los ítems seleccionados');
                })
                .finally(() => {
                    setIsGeneratingReceipt(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetSelections]); // Only run when targetSelections changes



    const handleGoToPayment = () => {
        const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const user_id = user.user_id ? Number(user.user_id) : 0;
        setIsGeneratingReceipt(true);  // Start loading state for button


        if (pedido_group_id !== undefined) {
            // Handle pedido groups - similar to mesas
            createPaymentReceiptByPedidoGroupIdV2(pedido_group_id, selectedSplitMethod, user_id, total_persons, client_id, client_name || '').then((response) => {
                setReceipts(response.data);
                setGoToPayment(true);
            }).catch((error) => {
                console.error('Error creating payment receipt for group: ', error);
                setGoToPayment(false);
                toast.error('Error creando recibo de pago para el grupo');
            }).finally(() => {
                setIsGeneratingReceipt(false);  // End loading state
            });
        } else if (mesa_id !== undefined) {
  
            createPaymentReceiptByMesaIdV2(mesa_id, selectedSplitMethod, user_id, total_persons, undefined).then((response) => {
        
                const mesasPaymentCache = JSON.parse(localStorage.getItem('mesasPaymentCache') || '{}');
                mesa.charged = true;
                setReceipts(response.data);
                // set totalPersons to be the length of response.data
                mesasPaymentCache[mesa_id] = {
                    splitMethod: mesasPaymentCache[mesa_id]?.splitMethod || selectedSplitMethod,
                    totalPersons: response.data.length,
                    paidPersons: 0
                };
                localStorage.setItem('mesasPaymentCache', JSON.stringify(mesasPaymentCache));
                // IMPORTANT: Set goToPayment BEFORE dispatching event to prevent race condition
                setGoToPayment(true);
                // Dispatch event to update UI in real-time (after goToPayment is set)
                window.dispatchEvent(new CustomEvent('mesa-receipt-generated', { detail: { mesa_id } }));

            }).catch((error) => {
                console.error('Error creating payment receipt: ', error);
                setGoToPayment(false);
                toast.error('Error creando recibo de pago');
            }).finally(() => {
                setIsGeneratingReceipt(false);  // End loading state
            });
        } else if (pedido_type === "Delivery" && client_name !== undefined) {
            createPaymentReceiptByPedidoIdV2(pedido.pedido_id, selectedSplitMethod, user_id, client_id, client_name, pedido_type).then((response) => {
                if (response.status === 200 || response.status === 201) {
                    setReceipts(response.data);
                    const receipt_id = response?.data ? response.data[0].receipt_id : undefined;

                    // Use functional update to avoid race conditions
                    const updatePedidoWithReceipt = (prevPedidos: any) => {
                        return prevPedidos.map((p: any) => {
                            if (p.pedido_id === pedido.pedido_id) {
                                // Store in localStorage as backup
                                const receiptCache = JSON.parse(localStorage.getItem('pedidoReceiptCache') || '{}');
                                receiptCache[pedido.pedido_id] = receipt_id;
                                localStorage.setItem('pedidoReceiptCache', JSON.stringify(receiptCache));
                                return { ...p, receipt_id: receipt_id };
                            }
                            return p;
                        });
                    };

                    // Use external setPedidos if provided, otherwise use context
                    if (externalSetPedidos) {
                        externalSetPedidos(updatePedidoWithReceipt);
                    } else {
                        setPedidos(updatePedidoWithReceipt);
                    }

                    // Additional delayed update to ensure persistence against race conditions
                    setTimeout(() => {
                        const delayedUpdate = (prevPedidos: any) => {
                            return prevPedidos.map((p: any) => {
                                if (p.pedido_id === pedido.pedido_id) {
                                    return { ...p, receipt_id: receipt_id };
                                }
                                return p;
                            });
                        };
                        if (externalSetPedidos) {
                            externalSetPedidos(delayedUpdate);
                        } else {
                            setPedidos(delayedUpdate);
                        }
                    }, 500);

                    setGoToPayment(true);
                }
            }).catch((error: any) => {
                console.error('Error creating payment receipt: ', error);
                toast.error('Error creando recibo de pago');
                setGoToPayment(false);
            }).finally(() => {
                setIsGeneratingReceipt(false);  // End loading state
            });
        }
    };


    const handleSelection = (method: string) => {
        /** This method will allow to identify which table do not close after payment
         * sine custom and equal paid tables, require all of their receipts to be payed
         * before closing the table
        
         in case  the method is equal or custom, we need to set in localStorage a variable called
         mesasPaymentCache, with its value being a dictionary, with the keys being the mesa_id,
         and the value being the selectedSplitMethod and totalPeople  */

        const mesasPaymentCache = JSON.parse(localStorage.getItem('mesasPaymentCache') || '{}');
        if (mesa_id !== undefined) {
            mesasPaymentCache[mesa_id] = {
                splitMethod: method,
                totalPersons: method !== 'single' ? total_persons : 1,
                paidPersons: 0
            };
        }
        localStorage.setItem('mesasPaymentCache', JSON.stringify(mesasPaymentCache));
        setSelectedSplitMethod(method);

        // Reset total_persons when switching methods
        if (method !== 'equal') {
            setTotalPersons(1);
        }
    };

    return (
        <div className='split-method-component'>
            {/* Back Button */}
            {onHide && (
                <button 
                    className="back-button"
                    onClick={onHide}
                    disabled={isGeneratingReceipt}
                    aria-label="Volver"
                >
                    <FaArrowLeft />
                    <span>Volver</span>
                </button>
            )}
            
            <div className="section-title">
                <FaTags className="title-icon" />
                Método de división
            </div>
            <Form.Group controlId="splitMethod" className='mp-formgroup horizontal'>
                <div className="pago-options-container">
                    {!hideUnicaOption && (
                        <div
                            className={`pago-option ${selectedSplitMethod === 'single' ? 'selected' : ''} ${isGeneratingReceipt ? 'disabled' : ''}`}
                            onClick={() => !isGeneratingReceipt && handleSelection('single')}
                        >
                            <FaReceipt className="icono-pago" />
                            <div className="option-text">
                                <strong>Una Cuenta</strong>
                                <p className="description">Todo el pago será cargado a una sola cuenta.</p>
                            </div>
                        </div>
                    )}


                    <div
                        className={`pago-option ${selectedSplitMethod === 'equal' ? 'selected' : ''} ${isGeneratingReceipt ? 'disabled' : ''}`}
                        onClick={() => !isGeneratingReceipt && handleSelection('equal')}
                    >
                        <FaHandHoldingUsd className="icono-pago" />
                        <div className="option-text">
                            <strong>Partes Iguales</strong>
                            <p className="description">El pago se dividirá equitativamente entre varias personas.</p>
                        </div>
                    </div>
                    {shouldShowPersonalizado() && (
                        <div
                            className={`pago-option ${selectedSplitMethod === 'custom' ? 'selected' : ''} ${isGeneratingReceipt ? 'disabled' : ''}`}
                            onClick={() => !isGeneratingReceipt && handleSelection('custom')}
                        >
                            <FaUsers className="icono-pago" />
                            <div className="option-text">
                                <strong>Personalizado</strong>
                                <p className="description">El pago se hará de manera personalizada.</p>
                            </div>
                        </div>
                    )}
                </div>
            </Form.Group>
            {selectedSplitMethod === 'equal' && (
                <div className="person-count-section">
                    <div className="person-quick-buttons">
                        {[2, 3, 4, 6].map(count => (
                            <Button
                                key={count}
                                variant="none"
                                className={total_persons === count ? 'clicked' : 'unclicked'}
                                onClick={() => !isGeneratingReceipt && setTotalPersons(count)}
                                disabled={isGeneratingReceipt}
                            >
                                {count}
                            </Button>
                        ))}
                    </div>
                    
                    <div className="person-input-wrapper">
                        <label htmlFor="totalPersons">Total de Personas</label>
                        <input
                            id="totalPersons"
                            className="form-control"
                            type="text"
                            min="2"
                            max="20"
                            value={total_persons > 1 ? total_persons : ''}
                            placeholder="Ej: 4"
                            disabled={isGeneratingReceipt}
                            onChange={(e) => {
                                if (!isGeneratingReceipt) {
                                    const value = Number(e.target.value);
                                    setTotalPersons(value);
                                }
                            }}
                        />
                    </div>
                </div>
            )}
            {shouldShowWarning() && (
                <div className="warning-section">
                    <FaExclamationTriangle className="warning-icon" />
                    <span>Los pedidos no han sido asignados a los comensales</span>
                </div>
            )}
            {selectedSplitMethod && (
                <div>
                    <Button
                        variant='none'
                        onClick={handleGoToPayment}
                        className={`generate-receipt-btn ${(!isGenerarCuentaEnabled() || isGeneratingReceipt) ? 'disabled' : ''}`}
                        disabled={!isGenerarCuentaEnabled() || isGeneratingReceipt}
                    >
                        {isGeneratingReceipt ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Generando...
                            </>
                        ) : (
                            <>
                                <FaFileInvoiceDollar />
                                Generar Cuenta
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};


export default SplitMethod;
